import { parse } from "tldts";
import { EntityItem } from "../common-types/Entity";
import { OBJECT_REFERENCE_REGEX } from "../../lib/FoundryMarkdown";

export function checkEnv(
  value: string | undefined,
  name: string,
): asserts value is string {
  if (value == null || value === "") {
    throw new Error(`Missing environment variable: ${name}`);
  }
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const getFormattedDate = (): string => {
  const today = new Date();
  const day = today.getDate();
  const month = today.toLocaleString("default", { month: "long" });
  const year = today.getFullYear();

  return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
};

export const getTodaysDate = () => {
  const today = new Date();
  const formattedDate = today.toISOString().split("T")[0];
  return formattedDate;
};

export const formatDate = (inputDate: string) => {
  const date = new Date(inputDate);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month}, ${year}`;
};

/**
 * Copies the given text to the clipboard.
 * @param text - The text to be copied.
 * @returns A Promise that resolves when the text is copied successfully.
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

/**
 * Removes button and SVG elements from an HTML element using DOM mutation.
 * Clones the element to avoid mutating the original.
 * @param element - The HTML element to clean.
 * @returns The innerHTML string with buttons and SVGs removed.
 */
export function cleanHtmlForCopy(element: HTMLElement): string {
  const cloned = element.cloneNode(true) as HTMLElement;

  // Remove elements marked as non-copyable (TOC, map, etc.)
  const noCopy = cloned.querySelectorAll("[data-no-copy]");
  noCopy.forEach((el) => el.remove());

  // Remove all button elements
  const buttons = cloned.querySelectorAll("button");
  buttons.forEach((button) => button.remove());

  // Remove all SVG elements
  const svgs = cloned.querySelectorAll("svg");
  svgs.forEach((svg) => svg.remove());

  return cloned.innerHTML;
}
export interface UrlValues {
  targetObjectType?: string;
  primaryKey?: string;
}

export function extractUrlValues(href: string): UrlValues {
  // URL format is either:
  // - /${objectTypeWithoutOntology}/${primaryKey}
  // - /link/${objectTypeWithoutOntology}/${primaryKey}
  try {
    // Remove leading slash and split by slashes
    const cleanPath = href.startsWith("/") ? href.slice(1) : href;
    const urlParts = cleanPath.split("/").filter((part) => part !== "");

    // If the path starts with 'link', skip that part
    const startIndex = urlParts[0] === "link" ? 1 : 0;

    return {
      targetObjectType: urlParts[startIndex],
      primaryKey: urlParts[startIndex + 1],
    };
  } catch (error) {
    console.error("Failed to parse URL", href, error);
    return {};
  }
}

export function getDomainFromUrl(url: string): string | null {
  try {
    const { domain } = parse(url);
    return domain;
  } catch (error) {
    console.error("Invalid URL passed to getDomainFromUrl:", url, error);
    return null;
  }
}

const INTERNAL_SOURCE_TYPES = new Set(["AFP", "Opoint"]);

export const isInternalEvent = (sourceType?: string | null): boolean =>
  INTERNAL_SOURCE_TYPES.has(sourceType ?? "");

export const getInternalSourceLabel = (sourceType?: string | null): string =>
  sourceType ? `Unruly / ${sourceType}` : "Unruly";

export function extractReferencedEvents(text: string): EntityItem[] {
  const regex =
    /:objectreference\[(.*?)\]\{objectType="(.*?)" primaryKey="(.*?)"\} - (\d{4}-\d{2}-\d{2})/g;

  const matches: EntityItem[] = [];

  let match;
  while ((match = regex.exec(text)) !== null) {
    const [_, name, objectType, id, date] = match;
    matches.push({
      name,
      objectType,
      id,
      description: date,
      type: "event",
    });
  }

  return matches;
}

export function extractParagraphBeforeReferences(text: string): string {
  const [before] = text.split(/Referenced Events:/);
  return before.trim();
}

export function extractFoundryObjectReferences(text: string): EntityItem[] {
  const matches: EntityItem[] = [];

  let match;
  while ((match = OBJECT_REFERENCE_REGEX.exec(text)) !== null) {
    const [_, name, objectType, id] = match;
    matches.push({
      name,
      objectType,
      id,
      description: "",
      type: "event",
    });
  }

  return matches;
}

/**
 * Assigns incrementing source numbers to event IDs based on order of first
 * appearance. Returns a Map<eventId, number> starting at 1.
 */
export function buildSourceNumberMap(
  referencedEvents: EntityItem[],
): Map<string, number> {
  const map = new Map<string, number>();
  let counter = 1;
  for (const { id } of referencedEvents) {
    if (!map.has(id)) {
      map.set(id, counter++);
    }
  }
  return map;
}
